package no.vareflow.web

// Minimal typed helpers over the browser DOM. Kept dependency-free on purpose: the page is plain
// HTML + CSS, so browsers' own features (password managers, zoom, Ctrl+F, screen readers) work.

external val document: dynamic
external val window: dynamic

/** A DOM element with small builder helpers. */
class El(val node: dynamic) {
    fun cls(value: String): El { node.className = value; return this }
    fun text(value: String): El { node.textContent = value; return this }
    fun attr(name: String, value: String): El { node.setAttribute(name, value); return this }
    fun on(event: String, handler: (dynamic) -> Unit): El { node.addEventListener(event, handler); return this }
    fun onClick(handler: () -> Unit): El = on("click") { handler() }
    fun disabled(value: Boolean): El { node.disabled = value; return this }
    fun add(child: El): El { node.appendChild(child.node); return child }

    fun el(tag: String, cls: String = "", text: String? = null, build: El.() -> Unit = {}): El {
        val child = El(document.createElement(tag))
        if (cls.isNotEmpty()) child.node.className = cls
        if (text != null) child.node.textContent = text
        node.appendChild(child.node)
        child.build()
        return child
    }

    fun div(cls: String = "", text: String? = null, build: El.() -> Unit = {}) = el("div", cls, text, build)
    fun span(cls: String = "", text: String? = null, build: El.() -> Unit = {}) = el("span", cls, text, build)
    fun h1(text: String, cls: String = "") = el("h1", cls, text)
    fun h2(text: String, cls: String = "") = el("h2", cls, text)
    fun p(text: String, cls: String = "") = el("p", cls, text)

    fun button(text: String, cls: String = "btn", enabled: Boolean = true, onClick: () -> Unit): El =
        el("button", cls, text) {
            attr("type", "button")
            node.disabled = !enabled
            onClick { if (enabled) onClick() }
        }

    /** Text input; [key] keeps focus and caret across redraws. */
    fun input(
        value: String,
        key: String,
        cls: String = "input",
        placeholder: String = "",
        type: String = "text",
        onInput: (String) -> Unit = {}
    ): El = el("input", cls) {
        attr("type", type)
        attr("data-key", key)
        if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
        node.value = value
        on("input") { onInput(node.value as String) }
    }

    fun clear(): El { node.innerHTML = ""; return this }

    /** Inline SVG from trusted, hard-coded markup only. */
    fun svg(markup: String, cls: String = ""): El = el("span", cls) { node.innerHTML = markup }
}

fun byId(id: String): El? {
    val node: dynamic = document.getElementById(id)
    return if (node == null) null else El(node)
}

fun setTimeout(ms: Int, action: () -> Unit): Int = window.setTimeout(action, ms) as Int
fun clearTimeout(handle: Int) { window.clearTimeout(handle) }
